import { RiMusic2Line, RiDeleteBinLine, RiDownloadLine, RiDraggable, RiArrowUpDoubleLine, RiVolumeUpLine, RiPlayFill } from '@remixicon/react'
import { Button } from '@/components/ui/button'

export interface Song {
	id: string
	name: string
	artist: string | null
	file: string | null
	spotifyUri: string | null
	index: number
}

export function SongRowContent({
	song,
	index,
	accentColor,
	isDragOverlay,
	isPlaceholder,
	isCurrent,
	dragHandleProps,
	onRemove,
	isRemoving,
	onMoveToTop,
	onPlay,
}: {
	song: Song
	index: number
	accentColor: string
	isDragOverlay?: boolean
	isPlaceholder?: boolean
	isCurrent?: boolean
	dragHandleProps?: Record<string, Function>
	onRemove?: () => void
	isRemoving?: boolean
	onMoveToTop?: () => void
	onPlay?: () => void
}) {
	const needsDownload = !song.file && !!song.spotifyUri
	const canPlay = onPlay && !needsDownload && !!song.file && !isDragOverlay && !isPlaceholder

	return (
		<div
			className={[
				'group/row flex items-center gap-3 px-2 py-2.5',
				isPlaceholder
					? 'opacity-30'
					: isDragOverlay
						? 'bg-card shadow-xl shadow-primary/5 rounded border border-primary/40 ring-1 ring-primary/10'
						: isCurrent
							? 'border-b border-border/30 rounded-sm bg-card/60'
							: 'border-b border-border/30 transition-colors hover:bg-card/40',
			].join(' ')}
		>
			{/* Track number / now playing indicator */}
			{dragHandleProps ? (
				<button
					type="button"
					className="relative flex size-6 shrink-0 cursor-grab items-center justify-center active:cursor-grabbing"
					{...dragHandleProps}
				>
					<span className="text-[11px] tabular-nums text-muted-foreground/40 transition-opacity group-hover/row:opacity-0">
						{index + 1}
					</span>
					<RiDraggable
						className="absolute size-3.5 text-muted-foreground/40 opacity-0 transition-opacity group-hover/row:opacity-100"
					/>
				</button>
			) : (
				<div className="flex size-6 shrink-0 items-center justify-center">
					{isCurrent ? (
						<RiVolumeUpLine className="size-3.5" style={{ color: accentColor }} />
					) : (
						<span className="text-[11px] tabular-nums text-muted-foreground/40">
							{index + 1}
						</span>
					)}
				</div>
			)}

			{/* Song icon — morphs to play button on hover */}
			{canPlay ? (
				<button
					type="button"
					className="group/icon relative flex size-9 shrink-0 items-center justify-center rounded transition-colors"
					style={{
						backgroundColor: `color-mix(in oklch, ${accentColor} 8%, transparent)`,
					}}
					onClick={onPlay}
				>
					<RiMusic2Line
						className="size-3.5 transition-opacity group-hover/icon:opacity-0"
						style={{ color: accentColor }}
					/>
					<RiPlayFill
						className="absolute size-3.5 opacity-0 transition-opacity group-hover/icon:opacity-100"
						style={{ color: accentColor }}
					/>
				</button>
			) : (
				<div
					className="flex size-9 shrink-0 items-center justify-center rounded"
					style={needsDownload ? undefined : {
						backgroundColor: `color-mix(in oklch, ${accentColor} 8%, transparent)`,
					}}
				>
					{needsDownload ? (
						<RiDownloadLine className="size-3.5 text-amber-500" />
					) : (
						<RiMusic2Line className="size-3.5" style={{ color: accentColor }} />
					)}
				</div>
			)}

			{/* Song info */}
			<div className="min-w-0 flex-1">
				<p className="truncate text-[13px] font-medium leading-tight" style={isCurrent ? { color: accentColor } : undefined}>{song.name}</p>
				{song.artist && (
					<p className="mt-0.5 truncate text-[11px] text-muted-foreground/60">{song.artist}</p>
				)}
				{needsDownload && (
					<p className="mt-0.5 text-[11px] text-amber-500/70">Pending download</p>
				)}
			</div>

			{/* Actions */}
			{!isDragOverlay && onMoveToTop && (
				<Button
					variant="ghost"
					size="icon-xs"
					className="text-muted-foreground/0 transition-colors group-hover/row:text-muted-foreground hover:!text-foreground"
					onClick={onMoveToTop}
				>
					<RiArrowUpDoubleLine className="size-3" />
				</Button>
			)}
			{!isDragOverlay && onRemove && (
				<Button
					variant="ghost"
					size="icon-xs"
					className="text-muted-foreground/0 transition-colors group-hover/row:text-muted-foreground hover:!text-destructive"
					onClick={onRemove}
					disabled={isRemoving}
				>
					<RiDeleteBinLine className="size-3" />
				</Button>
			)}
		</div>
	)
}
